import type { WhatsNewEntry } from '../types';

/**
 * Which entries a person has already been shown, remembered in this browser.
 *
 * ⚠️ PER BROWSER, NOT PER PERSON, FOR NOW. It is `localStorage`, so a second computer starts
 * from "nothing seen" and clearing site data does too. That costs one extra look at the feed
 * and no migration, which is the trade for shipping the feed before the table that would make
 * it follow the person. Everything touching storage is wrapped, because a private window or
 * blocked site data makes `localStorage` throw, and a missing dot must never break the header.
 */

/** On a browser with nothing stored, only entries this recent count as new, so a first visit shows a handful and not the whole history. */
export const FIRST_VISIT_DAYS = 14;

export const seenKey = (userId: string) => `pt.whats-new.seen.${userId}`;

/** Whole days from `from` to `to`, both `YYYY-MM-DD`. Calendar dates, so no time zone or daylight saving can move the answer. */
export function daysBetween(from: string, to: string): number {
  const utc = (d: string) => {
    const [y, m, day] = d.split('-').map(Number);
    return Date.UTC(y, m - 1, day);
  };
  return Math.round((utc(to) - utc(from)) / 86_400_000);
}

/**
 * The ids of the entries still to be shown. `seen` is null when nothing is stored for this
 * browser. An entry dated ahead of today is counted as news: nothing holds an entry back until its date.
 */
export function unreadIds(
  entries: readonly WhatsNewEntry[],
  seen: ReadonlySet<string> | null,
  today: string,
): string[] {
  return entries
    .filter((e) => (seen ? !seen.has(e.id) : daysBetween(e.date, today) <= FIRST_VISIT_DAYS))
    .map((e) => e.id);
}

type ReadStorage = Pick<Storage, 'getItem'>;
type WriteStorage = Pick<Storage, 'setItem'>;

/** The stored ids, or null when there is nothing usable (never stored, unreadable, or not a list of strings). */
export function readSeen(storage: ReadStorage | null, userId: string): Set<string> | null {
  try {
    const raw = storage?.getItem(seenKey(userId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed) || !parsed.every((v) => typeof v === 'string')) return null;
    return new Set(parsed);
  } catch {
    return null;
  }
}

/** Remember `ids`. Silent when storage is unavailable: the dot then clears for this visit only. */
export function writeSeen(storage: WriteStorage | null, userId: string, ids: Iterable<string>): void {
  try {
    storage?.setItem(seenKey(userId), JSON.stringify([...ids]));
  } catch {
    // Private window, blocked site data or a full quota: nothing to do but carry on.
  }
}

/** `localStorage`, or null where even asking for it throws. */
export function browserStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}
